/**
 * Client-side fallback for identity coaching (mirrors backend).
 */

const FIELD_META = {
  shoppingFor: {
    question: "Are you shopping for yourself, a gift, your household, or work?",
    why: "We weight budget and features differently depending on who the product is for.",
  },
  budgetMax: {
    question: "What's your typical max budget for products like this?",
    why: "Budget is the single biggest factor in a trustworthy match score.",
  },
  lifeRole: {
    question: "Are you a student, professional, parent, or enthusiast?",
    why: "Your role helps us know which specs to prioritize.",
  },
  techRelationship: {
    question: "Do you use tech daily, occasionally, as an enthusiast, or professionally?",
    why: "This shapes whether we favor simplicity, power, or pro-grade features.",
  },
};

export function buildIdentityCoachFallback({
  identityCompleteness = null,
  productName = null,
  categoryName = null,
  lowConfidence = false,
  reason = "identity_required",
}) {
  const missing = identityCompleteness?.missing || [];
  const percent = identityCompleteness?.percent ?? 0;
  const productRef = productName ? ` for ${productName}` : "";
  const categoryRef = categoryName ? ` ${categoryName}` : " products";

  if (reason === "low_confidence" || (lowConfidence && identityCompleteness?.isReady)) {
    return {
      headline: `We need a bit more context${productRef} before this match score can be fully trusted.`,
      progressPercent: percent,
      questions: [
        {
          field: "useCase",
          question: `What will you mainly use this${categoryRef} for?`,
          why: "Use case is the fastest way to sharpen your match score.",
        },
        {
          field: "budget",
          question: "What's your budget range for this purchase?",
          why: "A clear budget lets us score value and fit accurately.",
        },
      ],
      actionLabel: "Refine in chat",
      source: "fallback",
    };
  }

  const questions = missing
    .filter((field) => FIELD_META[field])
    .map((field) => ({
      field,
      question: FIELD_META[field].question,
      why: FIELD_META[field].why,
    }));

  if (!questions.length && !identityCompleteness?.isReady) {
    questions.push(
      { field: "budgetMax", ...FIELD_META.budgetMax },
      { field: "shoppingFor", ...FIELD_META.shoppingFor },
    );
  }

  const headline =
    percent > 0 && percent < 100
      ? `Your shopping profile is ${percent}% complete — finish the items below to unlock a trustworthy match score${productRef}.`
      : `Tell us how you shop and what you typically spend${productRef} — then we can score this product accurately.`;

  return {
    headline: headline.slice(0, 400),
    progressPercent: percent,
    questions: questions.slice(0, 3),
    actionLabel: "Complete shopping profile",
    source: "fallback",
  };
}

export { FIELD_META };
